import React, { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import Drawer from '../UI/Drawer';
import ConfirmModal from '../UI/ConfirmModal';
import { errMessage, inputCls, inputStyle, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle } from '../UI/ink';
import { notificationsApi } from '../../api/notifications';
import { EVENTS, LOG_STATUS, SEVERITY_CHOICES, clock } from './model';
import { Notice, Switch, kicker } from './ui';

const Step = ({ n, title, children }) => (
  <div className="flex flex-col gap-2.5">
    <span className="flex items-center gap-2.5"><span className="w-6 h-6 box-border flex items-center justify-center rounded-full border font-mono text-[11px]" style={{ borderColor: 'var(--surf-line-strong)' }}>{n}</span><span className="text-[14.5px] font-semibold">{title}</span></span>
    {children}
  </div>
);
const Ok = ({ ok, children }) => (
  <span className="flex items-start gap-2 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
    {ok ? <Check className="w-[15px] h-[15px] shrink-0 mt-px" strokeWidth={2.4} style={{ color: 'var(--sev-good-fg)' }} /> : <X className="w-[15px] h-[15px] shrink-0 mt-px" strokeWidth={2.4} style={{ color: 'var(--sev-crit-fg)' }} />}<span>{children}</span>
  </span>
);
const hint = 'text-xs leading-normal';

/**
 * Connect Telegram in three steps: bot, chat, what to send. The token is
 * write-only: the Worker stores it encrypted and never sends it back.
 */
export default function TelegramDrawer({ open, onClose, onChanged }) {
  const [ch, setCh] = useState(null);
  const [tokenIn, setTokenIn] = useState('');
  const [chatId, setChatId] = useState('');
  const [minSev, setMinSev] = useState('high');
  const [events, setEvents] = useState(null); // null = all
  const [enabled, setEnabled] = useState(true);
  const [check, setCheck] = useState(null);
  const [chats, setChats] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmOff, setConfirmOff] = useState(false);

  const load = async () => {
    const list = await notificationsApi.listChannels().catch(() => []);
    const tg = list.find(c => c.type === 'telegram') || null;
    setCh(tg);
    if (tg) { setChatId(tg.chat_id); setMinSev(tg.min_severity); setEvents(tg.event_types); setEnabled(tg.enabled); }
    notificationsApi.listLog(6).then(setLog).catch(() => setLog([]));
    return tg;
  };
  useEffect(() => {
    if (!open) return;
    setTokenIn(''); setCheck(null); setChats(null); setError(null); setNotice(null);
    setChatId(''); setMinSev('high'); setEvents(null); setEnabled(true);
    load();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn) => {
    setBusy(key); setError(null); setNotice(null);
    try { return await fn(); } catch (err) { setError(errMessage(err, 'Something went wrong.')); return null; } finally { setBusy(null); }
  };
  const doCheck = () => run('check', async () => setCheck(await notificationsApi.checkTelegram({ bot_token: tokenIn || undefined, chat_id: chatId || undefined })));
  const doFind = () => run('find', async () => {
    const found = await notificationsApi.findTelegramChats({ bot_token: tokenIn || undefined });
    setChats(found);
    if (found.length === 1) setChatId(found[0].id);
  });
  const save = () => run('save', async () => {
    const saved = await notificationsApi.saveTelegram({ bot_token: tokenIn || undefined, chat_id: chatId, min_severity: minSev, event_types: events, enabled });
    setCh(saved); setTokenIn('');
    // Remember the bot and chat names on the saved channel.
    await notificationsApi.checkTelegram({ chat_id: chatId }).then(setCheck).catch(() => {});
    await load();
    setNotice('Saved.');
    onChanged?.();
  });
  const test = () => run('test', async () => {
    await notificationsApi.testTelegram();
    setNotice('Test message sent. Check the chat.');
    notificationsApi.listLog(6).then(setLog).catch(() => {});
    onChanged?.();
  });
  const disconnect = () => run('off', async () => {
    await notificationsApi.disconnectTelegram();
    setConfirmOff(false);
    onChanged?.();
    onClose();
  });

  const on = (k) => events === null || events.includes(k);
  const flip = (k) => {
    const cur = events === null ? EVENTS.map(([x]) => x) : events;
    const next = cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k];
    setEvents(next.length === EVENTS.length ? null : next);
  };
  const configured = !!ch?.configured;
  const canSave = chatId.trim() && (configured || tokenIn.trim());
  const botName = check?.bot?.ok ? check.bot.username : ch?.bot_username;
  const chatName = check?.chat?.ok ? check.chat.title : ch?.chat_title;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        kicker="INTEGRATION · ALERTS & CHAT"
        title="Telegram"
        subtitle="Alerts in a group chat, without watching the dashboard."
        footer={
          <>
            {configured ? <button type="button" onClick={() => setConfirmOff(true)} className="h-[42px] px-1 text-[13.5px] font-medium" style={{ color: 'var(--sev-crit-fg)' }}>Disconnect</button> : <span />}
            <div className="flex gap-2">
              {configured && <button type="button" onClick={test} disabled={!!busy} className={secondaryBtn} style={secondaryBtnStyle}>{busy === 'test' ? 'Sending…' : 'Send test'}</button>}
              <button type="button" onClick={save} disabled={!canSave || !!busy} className={primaryBtn} style={primaryBtnStyle}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
            </div>
          </>
        }
      >
        <div className="flex flex-col gap-[22px]">
          {error && <Notice>{error}</Notice>}
          {notice && <Notice kind="ok">{notice}</Notice>}

          <Step n="1" title="Bot">
            <label htmlFor="tg-token" className="text-[13px] font-medium">Bot token</label>
            <span className="flex gap-2">
              <input id="tg-token" type="password" autoComplete="off" value={tokenIn} onChange={e => { setTokenIn(e.target.value); setCheck(null); }} placeholder={configured ? 'Saved. Leave blank to keep it.' : '123456789:AA…'} className={`${inputCls} font-mono`} style={inputStyle} />
              <button type="button" onClick={doCheck} disabled={!!busy || (!tokenIn && !configured)} className={`${secondaryBtn} shrink-0`} style={secondaryBtnStyle}>{busy === 'check' ? 'Checking…' : 'Check'}</button>
            </span>
            {check?.bot && (check.bot.ok ? <Ok ok>Token works · bot <span className="font-mono">@{check.bot.username}</span></Ok> : <Ok ok={false}>{check.bot.error}</Ok>)}
            {!check?.bot && botName && <Ok ok>Bot <span className="font-mono">@{botName}</span></Ok>}
            <span className={hint} style={{ color: 'var(--ink-faint)' }}>Create a bot by messaging @BotFather in Telegram, then paste the token here. It’s stored encrypted and never shown again.</span>
          </Step>

          <Step n="2" title="Chat">
            <label htmlFor="tg-chat" className="text-[13px] font-medium">Chat ID</label>
            <span className="flex gap-2">
              <input id="tg-chat" type="text" value={chatId} onChange={e => { setChatId(e.target.value); setCheck(c => (c ? { bot: c.bot } : c)); }} placeholder="-1001234567890" className={`${inputCls} font-mono`} style={inputStyle} />
              <button type="button" onClick={doFind} disabled={!!busy || (!tokenIn && !configured)} className={`${secondaryBtn} shrink-0`} style={secondaryBtnStyle}>{busy === 'find' ? 'Looking…' : 'Find chat ID'}</button>
            </span>
            {chats && !chats.length && <span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>The bot hasn’t seen any chats yet. Add it to the group, send it /start, then try again.</span>}
            {chats && chats.length > 1 && (
              <div className="flex flex-col gap-1.5">
                {chats.map(c => (
                  <label key={c.id} className="flex items-center gap-2.5 px-3 py-2 rounded-[9px] border cursor-pointer text-[13px]" style={{ borderColor: chatId === c.id ? 'var(--accent)' : 'var(--surf-line)', background: 'var(--surf-bg)' }}>
                    <input type="radio" name="tg-chat-pick" checked={chatId === c.id} onChange={() => setChatId(c.id)} style={{ accentColor: 'var(--accent)' }} />
                    <span className="flex-grow">{c.title} <span style={{ color: 'var(--ink-faint)' }}>· {c.type}</span></span><span className="font-mono text-xs" style={{ color: 'var(--ink-faint)' }}>{c.id}</span>
                  </label>
                ))}
              </div>
            )}
            {check?.chat && (check.chat.ok
              ? <Ok ok={check.chat.can_post}>{check.chat.title} · {check.chat.type} · {check.chat.can_post ? 'bot can post' : 'the bot can’t post here yet'}</Ok>
              : <Ok ok={false}>{check.chat.error}</Ok>)}
            {!check?.chat && chatName && <Ok ok>{chatName}</Ok>}
            <span className={hint} style={{ color: 'var(--ink-faint)' }}>Add the bot to your group, send it /start, then press Find chat ID. Check confirms the bot can post there.</span>
          </Step>

          <Step n="3" title="What to send">
            <span id="tg-sev-l" className="text-[13px] font-medium">Lowest severity</span>
            <div role="group" aria-labelledby="tg-sev-l" className="flex gap-1">
              {SEVERITY_CHOICES.map(([id, t, tok]) => {
                const sel = minSev === id;
                return <button key={id} type="button" aria-pressed={sel} onClick={() => setMinSev(id)} className="flex-1 h-10 flex items-center justify-center gap-1.5 rounded-lg border text-[13px]" style={{ background: sel ? 'var(--surf-raised)' : 'var(--surf-bg)', borderColor: sel ? 'var(--accent)' : 'var(--surf-line-strong)' }}><span className="w-[7px] h-[7px] rounded-full" style={{ background: `var(--sev-${tok}-fg)` }} />{t}</button>;
              })}
            </div>
            <span className="mt-1.5 text-[13px] font-medium">Events</span>
            <div className="flex flex-col rounded-[10px] border" style={{ borderColor: 'var(--surf-line)' }}>
              {EVENTS.map(([k, t]) => (
                <div key={k} className="flex items-center gap-3 min-h-[52px] px-3 py-1 border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)' }}>
                  <span className="flex-grow flex flex-col gap-px"><span className="text-[13.5px]">{t}</span><span className="font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>{k}</span></span>
                  <Switch on={on(k)} onChange={() => flip(k)} label={`Send ${t}`} />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-[10px] border" style={{ borderColor: 'var(--surf-line)' }}>
              <span className="flex-grow text-[13.5px]">Sending is on</span><Switch on={enabled} onChange={setEnabled} label="Sending is on" />
            </div>
            <span className={hint} style={{ color: 'var(--ink-faint)' }}>Repeats, quiet hours and the full delivery log are in Alert routing.</span>
          </Step>

          <div className="flex flex-col gap-2">
            <h3 className={`m-0 ${kicker}`} style={{ color: 'var(--ink-faint)' }}>PREVIEW</h3>
            <div className="self-start max-w-[380px] flex flex-col gap-[5px] px-3.5 py-3 border text-[13px] leading-normal" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', borderRadius: '4px 14px 14px 14px' }}>
              <span className="font-semibold">🔴 CRITICAL — Detection finding on web-prod-01</span>
              <span>Asset: <span className="font-mono text-xs">web-prod-01</span></span>
              <span>C2 Connection Detected</span>
              <span className="underline" style={{ color: 'var(--ink-link)' }}>Open in DragonEye</span>
            </div>
            <span className={hint} style={{ color: 'var(--ink-faint)' }}>The format DragonEye sends today, with example values.</span>
          </div>

          <div className="flex flex-col">
            <h3 className={`m-0 mb-1 ${kicker}`} style={{ color: 'var(--ink-faint)' }}>RECENT DELIVERIES</h3>
            {!log.length && <span className="text-[13px] py-2" style={{ color: 'var(--ink-muted)' }}>Nothing sent yet.</span>}
            {log.map(l => {
              const [lbl, dot] = LOG_STATUS[l.status] || [l.status, 'var(--ink-faint)'];
              return (
                <div key={l.id} className="grid grid-cols-[64px_minmax(0,1fr)_auto] gap-2.5 items-center min-h-[40px] border-b text-[12.5px]" style={{ borderColor: 'var(--surf-line)' }}>
                  <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{clock(l.created_at)}</span>
                  <span className="truncate" title={l.error || ''}>{l.title}</span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap" style={{ color: 'var(--ink-muted)' }}><span className="w-[7px] h-[7px] rounded-full" style={{ background: dot }} />{lbl}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Drawer>
      <ConfirmModal
        isOpen={confirmOff}
        title="Disconnect Telegram?"
        message="DragonEye stops sending to this chat and forgets the bot token. The delivery log stays."
        confirmLabel={busy === 'off' ? 'Disconnecting…' : 'Disconnect'}
        danger
        disabled={busy === 'off'}
        onConfirm={disconnect}
        onCancel={() => setConfirmOff(false)}
      />
    </>
  );
}
